import { useCallback, useEffect, useMemo, useRef } from 'react'
import type { CSSProperties, PointerEvent as ReactPointerEvent } from 'react'
import {
  DEFAULT_LONG_PRESS,
  initialPress,
  step,
  type LongPressConfig,
  type PressEvent,
} from './long-press'

/**
 * The React end of the hold gesture. All the rules live in `long-press.ts`;
 * this is the wiring and the timer.
 *
 * IT RETURNS `style` AS WELL AS HANDLERS, which is not decoration. A long
 * press on Android raises the text-selection callout over the element being
 * held, so the gesture works and the page looks broken while it does. The
 * two lines that stop it belong with the gesture, not in the caller's
 * className where the next person to touch the markup can drop them.
 *
 * GIVE IT NO `onLongPress` AND IT ARMS NOTHING. The pill is held by
 * everybody and the orb is behind a capability gate, so somebody without it
 * must get the plain button back, not a dead gesture that buzzes and does
 * nothing.
 */

export interface LongPressHandlers {
  onPointerDown: (event: ReactPointerEvent) => void
  onPointerMove: (event: ReactPointerEvent) => void
  onPointerUp: () => void
  onPointerCancel: () => void
  onPointerLeave: () => void
  onContextMenu: (event: { preventDefault: () => void }) => void
  onClick: () => void
  style: CSSProperties
}

const GUARD_STYLE: CSSProperties = {
  WebkitTouchCallout: 'none',
  userSelect: 'none',
}

export function useLongPress({
  onLongPress,
  onTap,
  config = DEFAULT_LONG_PRESS,
}: {
  /** Omit to disable the gesture entirely. */
  onLongPress?: () => void
  onTap?: () => void
  config?: LongPressConfig
}): LongPressHandlers {
  const state = useRef(initialPress())
  const timer = useRef<number | null>(null)

  // Read through refs so the handlers are stable: a pill that re-binds its
  // pointer handlers mid-press would lose the press.
  const longPressRef = useRef(onLongPress)
  longPressRef.current = onLongPress
  const tapRef = useRef(onTap)
  tapRef.current = onTap

  const clearTimer = useCallback(() => {
    if (timer.current === null) return
    window.clearTimeout(timer.current)
    timer.current = null
  }, [])

  const dispatch = useCallback(
    (event: PressEvent) => {
      const next = step(state.current, event, config)
      state.current = next.state
      if (next.action === 'long-press') longPressRef.current?.()
      if (next.action === 'tap') tapRef.current?.()
    },
    [config]
  )

  // A press in flight when the pill unmounts — which is exactly what the
  // orb opening does to it — must not leave a timer to fire into nothing.
  useEffect(() => clearTimer, [clearTimer])

  return useMemo<LongPressHandlers>(
    () => ({
      onPointerDown: (event) => {
        if (!longPressRef.current) return
        clearTimer()
        dispatch({
          type: 'down',
          x: event.clientX,
          y: event.clientY,
          at: Date.now(),
        })
        timer.current = window.setTimeout(() => {
          timer.current = null
          dispatch({ type: 'elapsed', at: Date.now() })
        }, config.holdMs)
      },
      onPointerMove: (event) => {
        if (state.current.phase !== 'holding') return
        dispatch({ type: 'move', x: event.clientX, y: event.clientY })
        if (state.current.phase !== 'holding') clearTimer()
      },
      onPointerUp: () => {
        clearTimer()
        dispatch({ type: 'up' })
      },
      onPointerCancel: () => {
        clearTimer()
        dispatch({ type: 'cancel' })
      },
      onPointerLeave: () => {
        clearTimer()
        dispatch({ type: 'cancel' })
      },
      onContextMenu: (event) => {
        // Only while a hold is live: the right-click menu is nobody's enemy
        // the rest of the time.
        if (state.current.phase === 'idle') return
        event.preventDefault()
      },
      onClick: () => {
        dispatch({ type: 'click' })
      },
      style: GUARD_STYLE,
    }),
    [clearTimer, dispatch, config.holdMs]
  )
}
